/**
 * Shared ArtifactAccess model for buyer-facing cards (SYN-1565).
 *
 * Command consumes upstream state through this shape. `command-local-display`
 * is presentation-only and must never be treated as access truth.
 */

import { parseArtifactActivitySummary, type ArtifactActivitySummary } from "./activity.js";

export const ARTIFACT_ACCESS_SHAPE_VERSION = "artifact-access.v1" as const;

export type ArtifactAccessKind = "routine" | "connection" | "agent" | "skill";
// `public` (SYN-1902): a first-party, globally-visible community listing the
// caller can self-adopt. Presentation of the standing grant the BFF computed
// (ACSS §13.4) — never an authority the client re-derives.
export type ArtifactCatalogState = "discoverable" | "listed" | "hidden" | "public";
// `grabbable` (SYN-1902): publicly self-adoptable, no access request, no admin
// approval. The BFF emits it; the client renders it fail-closed.
export type ArtifactAccessState =
  | "yours"
  | "requestable"
  | "requested"
  | "assigned"
  | "denied"
  | "grabbable";
export type ArtifactSetupState = "ready" | "needs_setup" | "waiting" | "problem";
export type ArtifactAccessAuthoritySource = "atlas" | "coherence" | "command-local-display";

// `grab` (SYN-1902): one-click self-adopt of a community item. The grab ENDPOINT
// is SYN-1903; here it is only a parsed CTA value.
export type ArtifactAccessAction =
  | "request_access"
  | "add_to_role_grant_profile"
  | "finish_setup"
  | "run"
  | "review_and_run"
  | "grab"
  | "edit";

export type ConnectionOverallStatus =
  | "ready"
  | "available"
  | "needs_connection"
  | "needs_access"
  | "pending"
  | "needs_attention"
  | "service_issue"
  | "temporarily_unavailable"
  | "view_only";

export type ConnectionAccountAvailability =
  | "personal_connected"
  | "workspace_available"
  | "no_account_needed"
  | "account_required"
  | "account_attention"
  | "unknown";

export type ConnectionPrimaryActionKind =
  | "open_chat"
  | "add_and_use"
  | "add_and_connect"
  | "connect_account"
  | "request_access"
  | "view_request"
  | "reconnect"
  | "view_status"
  | "retry_status"
  | "view_details";

export type ConnectionExperienceReasonCode =
  | "canonical_target_missing"
  | "authority_unavailable"
  | "role_lookup_failed"
  | "no_eligible_role"
  | "credential_status_unavailable"
  | "request_state_unavailable"
  | "request_pending"
  | "request_approved"
  | "request_declined"
  | "administrator_setup_required"
  | "provider_unhealthy"
  | "policy_view_only"
  // Incident 2026-10-25 (R7): a problem signing in will not fix, and who will.
  | "administrator_consent_required"
  | "connection_needs_our_fix"
  | "provider_unavailable";

export interface ConnectionExperience {
  overallStatus: ConnectionOverallStatus;
  accountAvailability: ConnectionAccountAvailability;
  accessRequestStatus?: "pending" | "approved" | "declined";
  /** True when any role-scoped request is pending or approved, even if the
   * primary action is for a different role. */
  hasInFlightAccessRequest?: boolean;
  primaryAction: {
    kind: ConnectionPrimaryActionKind;
    label: string;
    enabled: boolean;
    connectionId: string | null;
    adoptionTargetId?: string;
    reasonCode?: ConnectionExperienceReasonCode;
    roleSelection: {
      mode: "none" | "implicit" | "required";
      candidateRoleIds: string[];
    };
  };
}

export interface ArtifactAccessRole {
  id?: string;
  key?: string;
  name: string;
}

export interface ArtifactAccess {
  shapeVersion: typeof ARTIFACT_ACCESS_SHAPE_VERSION;
  kind: ArtifactAccessKind;
  id: string;
  title: string;
  description?: string;
  catalogState: ArtifactCatalogState;
  accessState: ArtifactAccessState;
  setupState: ArtifactSetupState;
  authoritySource: ArtifactAccessAuthoritySource;
  roles: ArtifactAccessRole[];
  actions: ArtifactAccessAction[];
  connectionExperience?: ConnectionExperience;
  technicalDetails?: Record<string, unknown>;
  /** Per-card activity summary from the BFF enrichment (Task 8 / SYN-1966 W4). */
  activity?: ArtifactActivitySummary;
}

const KINDS = new Set<ArtifactAccessKind>(["routine", "connection", "agent", "skill"]);
const CATALOG_STATES = new Set<ArtifactCatalogState>([
  "discoverable",
  "listed",
  "hidden",
  "public",
]);
const ACCESS_STATES = new Set<ArtifactAccessState>([
  "yours",
  "requestable",
  "requested",
  "assigned",
  "denied",
  "grabbable",
]);
const SETUP_STATES = new Set<ArtifactSetupState>(["ready", "needs_setup", "waiting", "problem"]);
const AUTHORITY_SOURCES = new Set<ArtifactAccessAuthoritySource>([
  "atlas",
  "coherence",
  "command-local-display",
]);
const ACTIONS = new Set<ArtifactAccessAction>([
  "request_access",
  "add_to_role_grant_profile",
  "finish_setup",
  "run",
  "review_and_run",
  "grab",
  "edit",
]);

const CONNECTION_OVERALL_STATUSES = new Set<ConnectionOverallStatus>([
  "ready",
  "available",
  "needs_connection",
  "needs_access",
  "pending",
  "needs_attention",
  "service_issue",
  "temporarily_unavailable",
  "view_only",
]);
const CONNECTION_ACCOUNT_AVAILABILITIES = new Set<ConnectionAccountAvailability>([
  "personal_connected",
  "workspace_available",
  "no_account_needed",
  "account_required",
  "account_attention",
  "unknown",
]);
const CONNECTION_ACTION_KINDS = new Set<ConnectionPrimaryActionKind>([
  "open_chat",
  "add_and_use",
  "add_and_connect",
  "connect_account",
  "request_access",
  "view_request",
  "reconnect",
  "view_status",
  "retry_status",
  "view_details",
]);
const CONNECTION_REASON_CODES = new Set<ConnectionExperienceReasonCode>([
  "canonical_target_missing",
  "authority_unavailable",
  "role_lookup_failed",
  "no_eligible_role",
  "credential_status_unavailable",
  "request_state_unavailable",
  "request_pending",
  "request_approved",
  "request_declined",
  "administrator_setup_required",
  "provider_unhealthy",
  "policy_view_only",
  "administrator_consent_required",
  "connection_needs_our_fix",
  "provider_unavailable",
]);

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function parseRoles(value: unknown): ArtifactAccessRole[] {
  if (!Array.isArray(value)) {
    return [];
  }
  const roles: ArtifactAccessRole[] = [];
  for (const raw of value) {
    const record = asRecord(raw);
    if (typeof record.name !== "string" || record.name.length === 0) {
      continue;
    }
    roles.push({
      name: record.name,
      ...(typeof record.id === "string" && record.id.length > 0 ? { id: record.id } : {}),
      ...(typeof record.key === "string" && record.key.length > 0 ? { key: record.key } : {}),
    });
  }
  return roles;
}

function parseActions(value: unknown): ArtifactAccessAction[] {
  return Array.isArray(value)
    ? value.filter((action): action is ArtifactAccessAction => ACTIONS.has(action))
    : [];
}

function parseConnectionExperience(value: unknown): ConnectionExperience | null | undefined {
  if (value === undefined) {
    return undefined;
  }
  const experience = asRecord(value);
  const action = asRecord(experience.primaryAction);
  const roleSelection = asRecord(action.roleSelection);
  const candidateRoleIds = roleSelection.candidateRoleIds;
  const reasonCode = action.reasonCode;
  const accessRequestStatus = experience.accessRequestStatus;
  const hasInFlightAccessRequest = experience.hasInFlightAccessRequest;
  if (
    typeof experience.overallStatus !== "string" ||
    !CONNECTION_OVERALL_STATUSES.has(experience.overallStatus as ConnectionOverallStatus) ||
    typeof experience.accountAvailability !== "string" ||
    !CONNECTION_ACCOUNT_AVAILABILITIES.has(
      experience.accountAvailability as ConnectionAccountAvailability,
    ) ||
    typeof action.kind !== "string" ||
    !CONNECTION_ACTION_KINDS.has(action.kind as ConnectionPrimaryActionKind) ||
    typeof action.label !== "string" ||
    action.label.length === 0 ||
    typeof action.enabled !== "boolean" ||
    !(action.connectionId === null || typeof action.connectionId === "string") ||
    !(action.adoptionTargetId === undefined || typeof action.adoptionTargetId === "string") ||
    !(
      accessRequestStatus === undefined ||
      accessRequestStatus === "pending" ||
      accessRequestStatus === "approved" ||
      accessRequestStatus === "declined"
    ) ||
    !(hasInFlightAccessRequest === undefined || typeof hasInFlightAccessRequest === "boolean") ||
    !(
      reasonCode === undefined ||
      (typeof reasonCode === "string" &&
        CONNECTION_REASON_CODES.has(reasonCode as ConnectionExperienceReasonCode))
    ) ||
    !new Set(["none", "implicit", "required"]).has(String(roleSelection.mode)) ||
    !Array.isArray(candidateRoleIds) ||
    !candidateRoleIds.every((id): id is string => typeof id === "string" && id.length > 0)
  ) {
    return null;
  }
  return {
    overallStatus: experience.overallStatus as ConnectionOverallStatus,
    accountAvailability: experience.accountAvailability as ConnectionAccountAvailability,
    ...(typeof accessRequestStatus === "string"
      ? {
          accessRequestStatus: accessRequestStatus as "pending" | "approved" | "declined",
        }
      : {}),
    ...(typeof hasInFlightAccessRequest === "boolean" ? { hasInFlightAccessRequest } : {}),
    primaryAction: {
      kind: action.kind as ConnectionPrimaryActionKind,
      label: action.label,
      enabled: action.enabled,
      connectionId: action.connectionId as string | null,
      ...(typeof action.adoptionTargetId === "string"
        ? { adoptionTargetId: action.adoptionTargetId }
        : {}),
      ...(typeof reasonCode === "string"
        ? { reasonCode: reasonCode as ConnectionExperienceReasonCode }
        : {}),
      roleSelection: {
        mode: roleSelection.mode as "none" | "implicit" | "required",
        candidateRoleIds,
      },
    },
  };
}

export function parseArtifactAccess(raw: unknown): ArtifactAccess | null {
  const record = asRecord(raw);
  const kind = record.kind;
  const id = record.id;
  const title = record.title;
  const catalogState = record.catalogState;
  const accessState = record.accessState;
  const setupState = record.setupState;
  const authoritySource = record.authoritySource;
  const connectionExperience = parseConnectionExperience(record.connectionExperience);
  if (
    typeof kind !== "string" ||
    !KINDS.has(kind as ArtifactAccessKind) ||
    typeof id !== "string" ||
    id.length === 0 ||
    typeof title !== "string" ||
    title.length === 0 ||
    typeof catalogState !== "string" ||
    !CATALOG_STATES.has(catalogState as ArtifactCatalogState) ||
    typeof accessState !== "string" ||
    !ACCESS_STATES.has(accessState as ArtifactAccessState) ||
    typeof setupState !== "string" ||
    !SETUP_STATES.has(setupState as ArtifactSetupState) ||
    typeof authoritySource !== "string" ||
    !AUTHORITY_SOURCES.has(authoritySource as ArtifactAccessAuthoritySource) ||
    connectionExperience === null
  ) {
    return null;
  }
  return {
    shapeVersion: ARTIFACT_ACCESS_SHAPE_VERSION,
    kind: kind as ArtifactAccessKind,
    id,
    title,
    ...(typeof record.description === "string" ? { description: record.description } : {}),
    catalogState: catalogState as ArtifactCatalogState,
    accessState: accessState as ArtifactAccessState,
    setupState: setupState as ArtifactSetupState,
    authoritySource: authoritySource as ArtifactAccessAuthoritySource,
    roles: parseRoles(record.roles),
    actions: parseActions(record.actions),
    ...(connectionExperience ? { connectionExperience } : {}),
    ...(record.technicalDetails && typeof record.technicalDetails === "object"
      ? { technicalDetails: asRecord(record.technicalDetails) }
      : {}),
    ...(() => {
      const td =
        record.technicalDetails && typeof record.technicalDetails === "object"
          ? asRecord(record.technicalDetails)
          : {};
      const activity = parseArtifactActivitySummary(td.activity);
      return activity ? { activity } : {};
    })(),
  };
}
